import { Err, Ok } from 'ts-results';
import type {
  Expression,
  ExpressionKindBinary,
  ExpressionKindUnary,
  Variable,
  VariableRef
} from './types';
import type { Result } from 'ts-results';

export type EvaluatedExpr =
  | {
      array: false;
      value: number;
    }
  | {
      array: true;
      value: number[];
    };

export type EvalResult = Result<EvaluatedExpr, EvalError>;

/**
 * Verify that the given expression result is numeric,
 * feed that predicate into the typesystem.
 */
export function isNum(arg: number | string): arg is number {
  return typeof arg === 'number';
}

/**
 * Verify that the given expression result is a numeric array,
 * feed that predicate into the typesystem.
 */
export function isNumArr(arg: number[] | string[]): arg is number[] {
  return arg.length === 0 || typeof arg[0] === 'number';
}

export type Path = (string | number)[];
export interface EvalError {
  path: Path;
  message: string;
}

type Variables = {
  [k: string]: Variable;
};

interface Ctx {
  vars: Variables;
  path: Path;
}

function pushPath(ctx: Ctx, key: string | number): Ctx {
  return {
    vars: ctx.vars,
    path: [...ctx.path, key]
  };
}

const ERROR_NON_NUMERIC =
  'Cannot perform arithmetic operation on non-numeric values';
const ERROR_LENGTH_MISMATCH =
  'Cannot perform arithmetic operation on arrays of different lenghts';

function error(ctx: Ctx, message: string): Err<EvalError> {
  return Err({
    path: [...ctx.path],
    message: message
  });
}

function evalVarRef(ctx: Ctx, ref: VariableRef): EvalResult {
  if (ref.variable in ctx.vars) {
    const v = ctx.vars[ref.variable];
    return Ok({
      array: true,
      value: v.value
    });
  } else {
    return error(ctx, `Variable '${ref.variable}' not declared`);
  }
}

/**
 * Evaluate arbitrary value that can be an expression argument.
 * Propagate evaluated expression path to potential error messages.
 */
function evalArg(
  ctx: Ctx,
  arg: number | number[] | Expression | VariableRef
): EvalResult {
  if (Array.isArray(arg)) {
    return Ok({
      array: true,
      type: 'float',
      value: arg
    });
  } else if (typeof arg === 'number') {
    return Ok({
      array: false,
      type: 'float',
      value: arg
    });
  }
  if ('variable' in arg) {
    return evalVarRef(ctx, arg);
  } else {
    return evalExpr(ctx, arg);
  }
}

/**
 * Evaluate Pulser expression.
 * Propagate evaluated expression path to potential error messages.
 */
function evalExpr(ctx: Ctx, expr: Expression): EvalResult {
  return evalArg(pushPath(ctx, 'lhs'), expr.lhs).andThen((lhs) => {
    if ('rhs' in expr) {
      return evalArg(pushPath(ctx, 'rhs'), expr.rhs).andThen((rhs) =>
        performExprBinary(ctx, expr.expression, lhs, rhs)
      );
    } else {
      return performExprUnary(ctx, expr.expression, lhs);
    }
  });
}

/**
 * Apply a fallable function to every element of an array.
 * Early returns on first failure.
 */
function tryMap<T, U, Err>(
  arr: T[],
  f: (v: T) => Result<U, Err>
): Result<U[], Err> {
  const resultArray: U[] = [];
  for (const v of arr) {
    const res = f(v);
    if (res.err) {
      return res;
    }
    resultArray.push(res.val);
  }
  return Ok(resultArray);
}

function evalNumericFnBinary(
  ctx: Ctx,
  lhs: EvaluatedExpr,
  rhs: EvaluatedExpr,
  f: (lhs: number, rhs: number) => number
): EvalResult {
  if (lhs.array && rhs.array) {
    // two arrays - check for lenght matching and perform element-wise operation
    if (!isNumArr(lhs.value) || !isNumArr(rhs.value)) {
      return error(ctx, ERROR_NON_NUMERIC);
    }
    if (lhs.value.length != rhs.value.length) {
      return error(ctx, ERROR_LENGTH_MISMATCH);
    }
    const rhsNumeric = rhs.value;
    return Ok({
      array: true,
      value: lhs.value.map((lhs, i) => f(lhs, rhsNumeric[i]))
    });
  } else if (!lhs.array && !rhs.array) {
    // two scalars - perform operation and return scalar
    if (!isNum(lhs.value) || !isNum(rhs.value)) {
      return error(ctx, ERROR_NON_NUMERIC);
    }
    return Ok({ array: false, value: f(lhs.value, rhs.value) });
  } else if (lhs.array && !rhs.array) {
    // array and scalar - perform element-wise operation by expanding scalar value
    if (!isNumArr(lhs.value) || !isNum(rhs.value)) {
      return error(ctx, ERROR_NON_NUMERIC);
    }
    const rhsValue = rhs.value;
    return Ok({ array: true, value: lhs.value.map((lhs) => f(lhs, rhsValue)) });
  } else if (!lhs.array && rhs.array) {
    // scalar and array - perform element-wise operation by expanding scalar value
    if (!isNum(lhs.value) || !isNumArr(rhs.value)) {
      return error(ctx, ERROR_NON_NUMERIC);
    }
    const lhsValue = lhs.value;
    return Ok({ array: true, value: rhs.value.map((rhs) => f(lhsValue, rhs)) });
  } else {
    // all scalar/array combinations should have been handled above
    throw new Error('Unreachable');
  }
}

function evalNumericFnUnary(
  ctx: Ctx,
  arg: EvaluatedExpr,
  f: (v: number) => number
): EvalResult {
  if (arg.array) {
    if (!isNumArr(arg.value)) {
      return error(ctx, ERROR_NON_NUMERIC);
    }
    return Ok({ array: true, value: arg.value.map((v) => f(v)) });
  } else {
    if (!isNum(arg.value)) {
      return error(ctx, ERROR_NON_NUMERIC);
    }
    return Ok({ array: false, value: f(arg.value) });
  }
}

const binaryFns: Record<
  Exclude<ExpressionKindBinary, 'index'>,
  (a: number, b: number) => number
> = {
  add: (a, b) => a + b,
  sub: (a, b) => a - b,
  mul: (a, b) => a * b,
  div: (a, b) => a / b,
  mod: (a, b) => a % b,
  pow: (a, b) => Math.pow(a, b)
};

const unaryFns: Record<ExpressionKindUnary, (v: number) => number> = {
  neg: (v) => -v,
  abs: Math.abs,
  floor: Math.floor,
  ceil: Math.ceil,
  round: Math.round,
  sqrt: Math.sqrt,
  exp: Math.exp,
  log2: Math.log2,
  log: Math.log,
  sin: Math.sin,
  cos: Math.cos,
  tan: Math.tan,
  tanh: Math.tanh
};

function performExprBinary(
  ctx: Ctx,
  expr: ExpressionKindBinary,
  lhs: EvaluatedExpr,
  rhs: EvaluatedExpr
): EvalResult {
  switch (expr) {
    case 'index':
      if (!lhs.array) {
        return error(ctx, 'Cannot index into a scalar value');
      }
      if (rhs.array) {
        if (!isNumArr(rhs.value)) {
          return error(ctx, 'Cannot index using non-numeric values');
        }
        const indexed = tryMap(rhs.value, (idx) => {
          if (idx >= lhs.value.length) {
            return error(ctx, 'Index out of bounds');
          }
          return Ok(lhs.value[idx]);
        });
        if (indexed.err) {
          return indexed;
        }
        return Ok({ array: true, value: indexed.val as typeof lhs.value });
      } else {
        if (!isNum(rhs.value)) {
          return error(ctx, 'Cannot index using non-numeric values');
        }
        if (rhs.value >= lhs.value.length) {
          return error(ctx, 'Index out of bounds');
        }
        return Ok({ array: false, value: lhs.value[rhs.value] });
      }
    default:
      return evalNumericFnBinary(ctx, lhs, rhs, binaryFns[expr]);
  }
}

function performExprUnary(
  ctx: Ctx,
  expr: ExpressionKindUnary,
  arg: EvaluatedExpr
): EvalResult {
  return evalNumericFnUnary(ctx, arg, unaryFns[expr]);
}

/**
 * Evaluate potentially parametrized value into a constant.
 */
export function evaluateParametrized(
  parametrized: number | number[] | Expression | VariableRef,
  vars: Variables
): Result<EvaluatedExpr, EvalError> {
  return evalArg({ path: [], vars }, parametrized);
}
